import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ScrollToTopButton from "./ScrollToTop";
import { FaWhatsapp } from "react-icons/fa";
import Navbar from "./Navbar";
import Meta from "../components/Meta";
import {
  Menu,
  MessageCircle,
  Mail,
  Phone,
  X,
  ExternalLink,
  FileText,
  Building2,
  Receipt,
  ArrowRight,
  Info,
  Calculator,
  CheckCircle,
  Building,
  DollarSign,
  BarChart3,
  Briefcase,
  MessageSquare,
  Heart,
  Copyright,
  Users,
  Shield,
  Globe,
  Award,
  TrendingUp,
  Factory,
  Utensils,
  Import,
  Home,
  Zap,
  BookOpen,
  Palette,
  Lightbulb,
  PiggyBank,
  Search,
  FolderOpen,
  Landmark,
} from "lucide-react";

const ServicesPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("business-registrations");
  const [isOpen, setIsOpen] = useState(false);

  const year = new Date().getFullYear();

  const handleWhatsApp = () => {
    window.open("https://wa.me/919934743600", "_blank");
  };

  const handleEmail = () => {
    window.location.href = "mailto:govind@cagovindsinha.com";
  };

  const handlePhone = () => {
    window.location.href = "tel:+919934743600";
  };

  const sections = [
    {
      id: "business-registrations",
      title: "Business Registrations",
      icon: Building,
    },
    { id: "ngo-registration", title: "NGO Registration", icon: Heart },
    {
      id: "trademark-registration",
      title: "Trademark Registration",
      icon: Shield,
    },
    {
      id: "audit-tax-management",
      title: "Audit and Tax Management",
      icon: Calculator,
    },
  ];

  const sectionThemes = {
    "business-registrations": {
      bg: "bg-gradient-to-br from-blue-50 to-indigo-100",
      cardBg: "bg-white",
      iconBg: "from-blue-500 to-indigo-600",
      hoverIconBg: "from-blue-600 to-indigo-700",
      titleColor: "text-blue-600",
      buttonBg: "from-blue-600 to-indigo-700",
      buttonHover: "from-blue-700 to-indigo-800",
      borderHover: "border-blue-200",
    },
    "ngo-registration": {
      bg: "bg-gradient-to-br from-emerald-50 to-teal-100",
      cardBg: "bg-white",
      iconBg: "from-emerald-500 to-teal-600",
      hoverIconBg: "from-emerald-600 to-teal-700",
      titleColor: "text-emerald-600",
      buttonBg: "from-emerald-600 to-teal-700",
      buttonHover: "from-emerald-700 to-teal-800",
      borderHover: "border-emerald-200",
    },
    "trademark-registration": {
      bg: "bg-gradient-to-br from-purple-50 to-pink-100",
      cardBg: "bg-white",
      iconBg: "from-purple-500 to-pink-600",
      hoverIconBg: "from-purple-600 to-pink-700",
      titleColor: "text-purple-600",
      buttonBg: "from-purple-600 to-pink-700",
      buttonHover: "from-purple-700 to-pink-800",
      borderHover: "border-purple-200",
    },
    "audit-tax-management": {
      bg: "bg-gradient-to-br from-orange-50 to-red-100",
      cardBg: "bg-white",
      iconBg: "from-orange-500 to-red-600",
      hoverIconBg: "from-orange-600 to-red-700",
      titleColor: "text-orange-600",
      buttonBg: "from-orange-600 to-red-700",
      buttonHover: "from-orange-700 to-red-800",
      borderHover: "border-orange-200",
    },
  };

  const services = {
    "business-registrations": [
      {
        title: "Foreign Company Registration",
        icon: Globe,
        description:
          "Complete registration process for foreign companies looking to establish presence in India.",
        points: [
          "Branch Office Setup",
          "Liaison Office Registration",
          "Subsidiary Company Formation",
          "Regulatory Compliance",
        ],
      },
      {
        title: "Private Limited Company",
        icon: Building,
        description:
          "Incorporate your private limited company with complete legal compliance and documentation.",
        points: [
          "Digital Signature Certificate",
          "Director Identification Number",
          "Name Reservation",
          "MOA & AOA Drafting",
        ],
      },
      {
        title: "Public Limited Company Registration",
        icon: TrendingUp,
        description:
          "Register your public limited company for large-scale business operations and public funding.",
        points: [
          "IPO Preparation",
          "SEBI Compliance",
          "Stock Exchange Listing",
          "Public Disclosure Requirements",
        ],
      },
      {
        title: "LLP Registration",
        icon: Users,
        description:
          "Limited Liability Partnership registration combining benefits of partnership and company structure.",
        points: [
          "Partner Identification",
          "LLP Agreement",
          "Designated Partner Consent",
          "Annual Filing",
        ],
      },
      {
        title: "Nidhi Company Registration",
        icon: PiggyBank,
        description:
          "Register Nidhi company for mutual benefit and financial services among members.",
        points: [
          "Member Registration",
          "Deposit Acceptance",
          "Loan Distribution",
          "RBI Compliance",
        ],
      },
      {
        title: "Producer Company Registration",
        icon: Factory,
        description:
          "Special company structure for agricultural and rural development activities.",
        points: [
          "Farmer Collective Formation",
          "Agricultural Business Setup",
          "Rural Development Projects",
          "Cooperative Benefits",
        ],
      },
      {
        title: "One Person Company Registration",
        icon: Briefcase,
        description:
          "Single-person company registration for individual entrepreneurs and small businesses.",
        points: [
          "Single Member Company",
          "Limited Liability Protection",
          "Perpetual Succession",
          "Nominee Appointment",
        ],
      },
      {
        title: "Partnership Registration",
        icon: Users,
        description:
          "Register your partnership firm with proper legal documentation and compliance.",
        points: [
          "Partnership Deed",
          "Partner Agreement",
          "Profit Sharing Structure",
          "Legal Protection",
        ],
      },
      {
        title: "GST Registration",
        icon: Receipt,
        description:
          "Goods and Services Tax registration for businesses with comprehensive compliance support.",
        points: [
          "GST Number Allocation",
          "Monthly Returns",
          "Input Tax Credit",
          "Compliance Management",
        ],
      },
      {
        title: "FSSAI Food License Registrations",
        icon: Utensils,
        description:
          "Food Safety and Standards Authority license for food business operations.",
        points: [
          "Food License Categories",
          "Safety Standards Compliance",
          "Regular Inspections",
          "Renewal Services",
        ],
      },
      {
        title: "Import Export Code (IEC)",
        icon: Import,
        description:
          "Essential code for businesses involved in international trade and commerce.",
        points: [
          "International Trade License",
          "Customs Clearance",
          "Export Benefits",
          "Trade Documentation",
        ],
      },
      {
        title: "RERA Registrations",
        icon: Home,
        description:
          "Real Estate Regulatory Authority registration for real estate projects and agents.",
        points: [
          "Project Registration",
          "Agent Registration",
          "Compliance Monitoring",
          "Buyer Protection",
        ],
      },
      {
        title: "MSME Udyam Registration",
        icon: Zap,
        description:
          "Micro, Small, and Medium Enterprise registration for government benefits and support.",
        points: [
          "Priority Sector Lending",
          "Subsidy Benefits",
          "Government Schemes",
          "Credit Facilities",
        ],
      },
      {
        title: "ISO Certification",
        icon: Award,
        description:
          "International Organization for Standardization certification for quality management.",
        points: [
          "Quality Management Systems",
          "International Standards",
          "Audit Preparation",
          "Certification Maintenance",
        ],
      },
      {
        title: "Business Registration Number",
        icon: FileText,
        description:
          "Unique business identification number for various regulatory and compliance purposes.",
        points: [
          "Unique Business Identity",
          "Regulatory Compliance",
          "Government Interface",
          "Digital Integration",
        ],
      },
    ],
    "ngo-registration": [
      {
        title: "Trust Registration",
        icon: Heart,
        description:
          "Register your charitable trust for social, educational, or religious purposes.",
        points: [
          "Trust Deed Preparation",
          "Trustee Appointment",
          "Tax Exemption Benefits",
          "12A & 80G Registration",
        ],
      },
      {
        title: "Society Registration",
        icon: Users,
        description:
          "Register your society under Societies Registration Act for non-profit activities.",
        points: [
          "Memorandum of Association",
          "Governing Body Formation",
          "Annual General Meetings",
          "Compliance Management",
        ],
      },
      {
        title: "Section-8 Company",
        icon: Building,
        description:
          "Non-profit company registration under Companies Act for charitable objectives.",
        points: [
          "Non-Profit Structure",
          "Corporate Governance",
          "Annual Compliance",
          "Tax Benefits",
        ],
      },
      {
        title: "FCRA Registration",
        icon: Globe,
        description:
          "Foreign Contribution Regulation Act registration for receiving foreign funds.",
        points: [
          "Foreign Funding Eligibility",
          "Compliance Requirements",
          "Annual Returns",
          "Government Approvals",
        ],
      },
    ],
    "trademark-registration": [
      {
        title: "Trademark Registration",
        icon: Shield,
        description:
          "Protect your brand identity with comprehensive trademark registration services.",
        points: [
          "Brand Name Protection",
          "Logo Registration",
          "International Filing",
          "Infringement Protection",
        ],
      },
      {
        title: "Copyright Registration",
        icon: Copyright,
        description:
          "Secure your creative works and intellectual property with copyright protection.",
        points: [
          "Literary Works",
          "Artistic Creations",
          "Software Copyright",
          "Digital Content Protection",
        ],
      },
      {
        title: "Design Registration",
        icon: Palette,
        description:
          "Protect your unique designs and visual elements from unauthorized use.",
        points: [
          "Industrial Design Protection",
          "Visual Element Security",
          "Commercial Design Rights",
          "Infringement Prevention",
        ],
      },
      {
        title: "Patent Registration",
        icon: Lightbulb,
        description:
          "Secure your inventions and innovative solutions with patent protection.",
        points: [
          "Innovation Protection",
          "Technical Documentation",
          "Patent Search",
          "International Filing",
        ],
      },
    ],
    "audit-tax-management": [
      {
        title: "ITR Filing",
        icon: Calculator,
        description:
          "Professional income tax return filing services for individuals and businesses.",
        points: [
          "Individual Tax Returns",
          "Business Tax Filing",
          "Tax Optimization",
          "Refund Processing",
        ],
      },
      {
        title: "NRI Taxation",
        icon: Globe,
        description:
          "Specialized tax services for Non-Resident Indians with global income management.",
        points: [
          "International Tax Planning",
          "DTAA Benefits",
          "Foreign Income Reporting",
          "Compliance Management",
        ],
      },
      {
        title: "Statutory Audit",
        icon: Search,
        description:
          "Mandatory audit services ensuring compliance with statutory requirements.",
        points: [
          "Financial Statement Audit",
          "Compliance Verification",
          "Risk Assessment",
          "Audit Reports",
        ],
      },
      {
        title: "ROC Filing",
        icon: FolderOpen,
        description:
          "Registrar of Companies filing services for corporate compliance requirements.",
        points: [
          "Annual Returns",
          "Board Resolutions",
          "Director Appointments",
          "Share Capital Changes",
        ],
      },
      {
        title: "GST Return",
        icon: Receipt,
        description:
          "Comprehensive GST return filing and compliance management services.",
        points: [
          "Monthly Returns",
          "Annual Returns",
          "Input Tax Credit",
          "Reconciliation Services",
        ],
      },
      {
        title: "TDS Return",
        icon: DollarSign,
        description:
          "Tax Deducted at Source return filing and compliance management.",
        points: [
          "TDS Calculations",
          "Return Filing",
          "Certificate Generation",
          "Compliance Tracking",
        ],
      },
      {
        title: "GST Audit",
        icon: BarChart3,
        description:
          "Specialized GST audit services ensuring compliance with tax regulations.",
        points: [
          "Transaction Verification",
          "Input Credit Validation",
          "Compliance Check",
          "Audit Reports",
        ],
      },
      {
        title: "Bank Audit",
        icon: Landmark,
        description:
          "Comprehensive bank audit services for financial institutions and compliance.",
        points: [
          "Financial Institution Audit",
          "Regulatory Compliance",
          "Risk Assessment",
          "Internal Controls",
        ],
      },
      {
        title: "NGO Audit",
        icon: Heart,
        description:
          "Specialized audit services for non-governmental organizations and trusts.",
        points: [
          "Fund Utilization Audit",
          "Donor Compliance",
          "Transparency Reports",
          "Regulatory Filing",
        ],
      },
      {
        title: "Tax Audit",
        icon: CheckCircle,
        description:
          "Comprehensive tax audit services for businesses exceeding specified turnover limits.",
        points: [
          "Revenue Verification",
          "Expense Analysis",
          "Tax Compliance",
          "Audit Certification",
        ],
      },
    ],
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      sections.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section.id);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <Meta
        title="Services - Govind Sinha & Co."
        description="Explore the wide range of professional services offered by Govind Sinha & Co., Chartered Accountants in Patna. From audit and taxation to financial advisory and compliance, we deliver expert solutions tailored to your business needs."
        canonical="https://cagovindsinha.com/services"
      />
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <Navbar />
        <div className="fixed z-50 bottom-6 right-6">
          {/* Contact Options */}
          <div
            className={`flex flex-col-reverse items-end space-y-reverse space-y-4 mb-4 transition-all duration-500 ease-out ${
              isOpen
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8 pointer-events-none"
            }`}
          >
            {/* WhatsApp */}
            <div className="relative group">
              <button
                onClick={handleWhatsApp}
                className="flex items-center justify-center text-white transition-all duration-300 transform rounded-full shadow-lg w-14 h-14 bg-gradient-to-r from-green-500 to-green-600 hover:shadow-xl hover:scale-110 hover:from-green-600 hover:to-green-700"
              >
                <FaWhatsapp className="w-6 h-6" />
              </button>
              <div className="absolute transition-opacity duration-300 transform -translate-y-1/2 opacity-0 pointer-events-none right-16 top-1/2 group-hover:opacity-100">
                <div className="px-3 py-1 text-sm text-white bg-gray-900 rounded-lg whitespace-nowrap">
                  WhatsApp
                  <div className="absolute transform -translate-y-1/2 border-4 border-transparent left-full top-1/2 border-l-gray-900"></div>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="relative group">
              <button
                onClick={handleEmail}
                className="flex items-center justify-center text-white transition-all duration-300 transform rounded-full shadow-lg w-14 h-14 bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-110 hover:from-blue-600 hover:to-blue-700"
              >
                <Mail className="w-6 h-6" />
              </button>
              <div className="absolute transition-opacity duration-300 transform -translate-y-1/2 opacity-0 pointer-events-none right-16 top-1/2 group-hover:opacity-100">
                <div className="px-3 py-1 text-sm text-white bg-gray-900 rounded-lg whitespace-nowrap">
                  Email
                  <div className="absolute transform -translate-y-1/2 border-4 border-transparent left-full top-1/2 border-l-gray-900"></div>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="relative group">
              <button
                onClick={handlePhone}
                className="flex items-center justify-center text-white transition-all duration-300 transform rounded-full shadow-lg w-14 h-14 bg-gradient-to-r from-purple-500 to-purple-600 hover:shadow-xl hover:scale-110 hover:from-purple-600 hover:to-purple-700"
              >
                <Phone className="w-6 h-6" />
              </button>
              <div className="absolute transition-opacity duration-300 transform -translate-y-1/2 opacity-0 pointer-events-none right-16 top-1/2 group-hover:opacity-100">
                <div className="px-3 py-1 text-sm text-white bg-gray-900 rounded-lg whitespace-nowrap">
                  Call Us
                  <div className="absolute transform -translate-y-1/2 border-4 border-transparent left-full top-1/2 border-l-gray-900"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Toggle Button */}
          <button
            onClick={toggleMenu}
            className={`flex items-center justify-center w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full shadow-2xl hover:shadow-3xl transform transition-all duration-500 ease-out hover:scale-105 ${
              isOpen ? "rotate-180" : "rotate-0"
            }`}
          >
            <div
              className={`transition-transform duration-300 ${
                isOpen ? "rotate-180" : "rotate-0"
              }`}
            >
              {isOpen ? (
                <X className="w-7 h-7" />
              ) : (
                <MessageSquare className="w-7 h-7" />
              )}
            </div>
          </button>

          {/* Backdrop blur when open */}
          {isOpen && (
            <div
              className="fixed inset-0 bg-black/5 backdrop-blur-[1px] -z-10"
              onClick={toggleMenu}
            />
          )}
        </div>

        {/* Services Page Header */}
        <div className="py-8 pt-20 bg-gradient-to-br from-slate-50 to-gray-100">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="mb-2 text-2xl font-bold text-gray-900 md:text-3xl">
                Our Services
              </h1>
              <p className="max-w-2xl mx-auto text-gray-600">
                Comprehensive business solutions with expert guidance every step
                of the way.
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Bar */}
        <div className="sticky z-50 bg-white border-b border-gray-200 shadow-sm top-16">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="flex py-3 space-x-6 overflow-x-auto">
              {sections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all duration-300 ${
                      activeSection === section.id
                        ? "bg-slate-800 text-white shadow-md"
                        : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span className="text-sm font-medium">{section.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Services Sections */}
        {sections.map((section) => {
          const theme = sectionThemes[section.id];
          const IconComponent = section.icon;

          return (
            <section
              key={section.id}
              id={section.id}
              className={`py-20 ${theme.bg}`}
            >
              <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div className="mb-16 text-center">
                  <div className="flex items-center justify-center mb-6">
                    <div
                      className={`flex items-center justify-center w-16 h-16 bg-gradient-to-br ${theme.iconBg} rounded-xl`}
                    >
                      <IconComponent
                        className="w-8 h-8 text-white"
                        strokeWidth={2}
                      />
                    </div>
                  </div>
                  <h2 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl">
                    {section.title}
                  </h2>
                  <p className="max-w-3xl mx-auto text-xl text-gray-600">
                    Professional services tailored to your specific needs with
                    expert guidance and comprehensive support.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {services[section.id].map((service, index) => {
                    const ServiceIconComponent = service.icon;
                    return (
                      <div
                        key={index}
                        className={`group ${theme.cardBg} rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 motion-preset-expand hover:${theme.borderHover} hover:-translate-y-1`}
                      >
                        <div
                          className={`flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br ${theme.iconBg} rounded-xl group-hover:${theme.hoverIconBg} transition-all duration-300`}
                        >
                          <ServiceIconComponent
                            className="w-8 h-8 text-white"
                            strokeWidth={2}
                          />
                        </div>

                        <h3
                          className={`mb-4 text-xl font-bold text-gray-900 group-hover:${theme.titleColor} transition-colors duration-300`}
                        >
                          {service.title}
                        </h3>

                        <p className="mb-6 leading-relaxed text-gray-600">
                          {service.description}
                        </p>

                        <ul className="mb-6 space-y-2">
                          {service.points.map((point, pointIndex) => (
                            <li
                              key={pointIndex}
                              className="flex items-start space-x-3 text-sm text-gray-500"
                            >
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="pt-6 border-t border-gray-100">
                          <Link
                            to={`/services/${service.title
                              .toLowerCase()
                              .replace(/ /g, "-")}`}
                            // onClick={() => window.scrollTo(0, 0)}
                            className={`w-full px-6 py-3 text-center bg-gradient-to-r ${theme.buttonBg} text-white rounded-lg font-medium hover:${theme.buttonHover} transition-all duration-300 transform hover:scale-105`}
                          >
                            Learn More
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          );
        })}

        {/* Call to Action */}
        <div className="py-20 bg-gradient-to-r from-slate-900 to-gray-800">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="mb-6 text-3xl font-bold text-white md:text-4xl">
                Ready to Get Started?
              </h2>
              <p className="max-w-2xl mx-auto mb-8 text-xl text-gray-300">
                Contact our experts today for personalized consultation and
                professional guidance for your business needs.
              </p>
              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <Link
                  to="/#contact"
                  className="px-8 py-4 font-bold text-gray-800 transition-colors duration-300 bg-white rounded-lg hover:bg-gray-100"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <footer className="py-16 text-white bg-gray-900">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="grid gap-8 mb-8 md:grid-cols-2 lg:grid-cols-4">
              {/* Company Info */}
              <div>
                <div className="mb-4 text-2xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-200 bg-clip-text">
                  Govind Sinha & Co.
                </div>
                <p className="mb-6 text-gray-400">
                  Your trusted partner for comprehensive chartered accountancy
                  services, delivering excellence with integrity and precision.
                </p>
                <div className="flex space-x-4">
                  <a
                    href="https://x.com/cagovindsinha"
                    className="flex items-center justify-center w-10 h-10 transition-colors bg-blue-600 rounded-full hover:bg-blue-700"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.linkedin.com/in/ca-govind-sinha-b92306173/"
                    className="flex items-center justify-center w-10 h-10 transition-colors bg-blue-800 rounded-full hover:bg-blue-900"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.facebook.com/sinha.govind"
                    className="flex items-center justify-center w-10 h-10 transition-colors bg-blue-600 rounded-full hover:bg-blue-700"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Services */}
              <div>
                <h3 className="mb-4 text-xl font-bold">Our Services</h3>
                <ul className="space-y-2 text-gray-400">
                  <li>
                    <a
                      href="#"
                      className="transition-colors hover:text-yellow-400"
                    >
                      Tax Advisory
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="transition-colors hover:text-yellow-400"
                    >
                      Audit & Assurance
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="transition-colors hover:text-yellow-400"
                    >
                      Business Registration
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="transition-colors hover:text-yellow-400"
                    >
                      Financial Planning
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="transition-colors hover:text-yellow-400"
                    >
                      Bookkeeping
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="transition-colors hover:text-yellow-400"
                    >
                      Business Consulting
                    </a>
                  </li>
                </ul>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="mb-4 text-xl font-bold">Quick Links</h3>
                <ul className="space-y-2 text-gray-400">
                  <li>
                    <Link
                      to="/"
                      className="transition-colors hover:text-yellow-400"
                    >
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/#about"
                      className="transition-colors hover:text-yellow-400"
                    >
                      About Us
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/services"
                      // onClick={() => window.scrollTo(0, 0)}
                      className="transition-colors hover:text-yellow-400"
                    >
                      Services
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/checklists"
                      // onClick={() => window.scrollTo(0, 0)}
                      className="transition-colors hover:text-yellow-400"
                    >
                      Checklists
                    </Link>
                  </li>
                  {/* <li>
                    <Link
                      to="/pay"
                      // onClick={() => window.scrollTo(0, 0)}
                      className="transition-colors hover:text-yellow-400"
                    >
                      Make Payment
                    </Link>
                  </li> */}
                  <li>
                    <Link
                      to="/#resources"
                      className="transition-colors hover:text-yellow-400"
                    >
                      Resources
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/#testimonials"
                      className="transition-colors hover:text-yellow-400"
                    >
                      Testimonials
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/#contact"
                      className="transition-colors hover:text-yellow-400"
                    >
                      Contact
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="mb-4 text-xl font-bold">Contact Info</h3>
                <div className="space-y-3 text-gray-400">
                  <div className="flex items-start space-x-2">
                    <svg
                      className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <a
                      href="https://maps.app.goo.gl/vxBFC4HWTFof4oGV8"
                      className="text-sm transition-colors hover:text-yellow-400"
                    >
                      7th Floor, Adharshila Complex, 503, beside Reserve Bank of
                      India(RBI, South Gandhi Maidan, Patna, Bihar 800001
                    </a>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg
                      className="flex-shrink-0 w-5 h-5 text-yellow-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <p>
                      <a
                        href="tel:+917673040500"
                        className="block text-sm transition-colors hover:text-yellow-400"
                      >
                        +91 7673040500
                      </a>

                      <a
                        href="tel:+919334743600"
                        className="block text-sm transition-colors hover:text-yellow-400"
                      >
                        +91 9934743600
                      </a>
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg
                      className="flex-shrink-0 w-5 h-5 text-yellow-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <a
                      href="mailto:govind@cagovindsinha.com"
                      className="text-sm transition-colors hover:text-yellow-400"
                    >
                      govind@cagovindsinha.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-gray-800">
              <div className="flex flex-col items-center justify-between md:flex-row">
                <p className="mb-4 text-sm text-gray-400 md:mb-0">
                  © {year} Govind Sinha & Co. All rights reserved.
                  <br />
                  Made by{" "}
                  <span className="relative inline-block group">
                    <a
                      href="mailto:amansinha@proton.me"
                      className="hover:text-yellow-400"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Aman Kumar Sinha.
                    </a>
                    <div className="absolute px-4 py-2 text-sm font-medium text-gray-800 transition-all duration-300 transform -translate-x-1/2 border shadow-lg opacity-0 bg-white/90 backdrop-blur-md rounded-xl -top-12 left-1/2 group-hover:opacity-100 group-hover:-translate-y-1 whitespace-nowrap border-white/20">
                      <span className="inline-flex items-center gap-2 ">
                        <Mail className="w-4 h-4" />
                        amansinha@proton.me
                      </span>
                      <div className="absolute w-2 h-2 transform rotate-45 -translate-x-1/2 border-b border-r bg-white/90 backdrop-blur-md -bottom-1 left-1/2 border-white/20"></div>
                    </div>
                  </span>
                </p>
                <div className="flex space-x-6 text-sm text-gray-400">
                  <Link
                    to="/privacy-policy"
                    // onClick={() => window.scrollTo(0, 0)}
                    className="transition-colors hover:text-yellow-400"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="/disclaimer"
                    // onClick={() => window.scrollTo(0, 0)}
                    className="transition-colors hover:text-yellow-400"
                  >
                    Disclaimer
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ServicesPage;
